import React, { useState, useEffect, useMemo } from 'react';
import api from './api';

const MakeKapan = () => {
    // View State
    const [tab, setTab] = useState('new'); 
    const [isSubmitting, setIsSubmitting] = useState(false); // <--- NEW: Prevents Double Click

    // Data State
    const [cuts, setCuts] = useState([]);
    const [kapanList, setKapanList] = useState([]); 
    const [selectedCut, setSelectedCut] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});
    
    const [rows, setRows] = useState([]);

    useEffect(() => {
        loadAvailableCuts();
        loadKapanStock();
    }, []);

    const loadAvailableCuts = async () => {
        try {
            const res = await api.get('/cuts');
            const active = res.data.filter(c => c.status !== 'Divided' && c.status !== 'Completed');
            setCuts(active);
        } catch (err) { console.error("Error loading cuts"); }
    };

    const loadKapanStock = async () => {
        try {
            const res = await api.get('/kapans');
            setKapanList(res.data);
        } catch (err) { console.error("Error loading kapan list"); }
    };

    // --- GROUPING LOGIC ---
    const groupedKapans = useMemo(() => {
        const groups = {};
        kapanList.forEach(k => {
            const id = k.cutId || 'Unknown';
            if (!groups[id]) {
                const parentDisplayName = k.fullKapanId.includes('-') 
                    ? k.fullKapanId.substring(0, k.fullKapanId.lastIndexOf('-')) 
                    : k.parentCutName;

                groups[id] = { parentName: parentDisplayName, items: [], totalPcs: 0, totalWeight: 0 };
            }
            groups[id].items.push(k);
            groups[id].totalPcs += k.pieces;
            groups[id].totalWeight += k.weight;
        });
        return groups;
    }, [kapanList]);

    const toggleGroup = (cutId) => {
        setExpandedGroups(prev => ({ ...prev, [cutId]: !prev[cutId] }));
    };

    // --- ACTIONS ---
    const handleCutSelect = (e) => {
        const cutId = e.target.value;
        const cut = cuts.find(c => c._id === cutId);
        setSelectedCut(cut);
        if (cut) {
            setRows([{ name: `${cut.cutName}1`, pieces: '', weight: '', description: '' }]);
        } else {
            setRows([]);
        }
    };

    const updateRow = (index, field, value) => {
        const newRows = [...rows];
        newRows[index][field] = value;
        setRows(newRows);
    };

    const addRow = () => {
        const nextNum = rows.length + 1;
        const nextName = `${selectedCut.cutName}${nextNum}`;
        setRows([...rows, { name: nextName, pieces: '', weight: '', description: '' }]);
    };

    const removeRow = (index) => {
        if(rows.length > 1) {
            setRows(rows.filter((_, i) => i !== index));
        }
    };

    const handleDeleteKapan = async (id) => {
        if(!window.confirm("Are you sure you want to delete this Kapan?")) return;
        try {
            await api.delete(`/kapans/${id}`);
            loadKapanStock(); // Refresh list immediately
        } catch (err) { alert("Error deleting: " + err.message); }
    };

    // --- CALCULATIONS ---
    const totalPcs = rows.reduce((acc, r) => acc + (parseInt(r.pieces) || 0), 0);
    const totalWeight = rows.reduce((acc, r) => acc + (parseFloat(r.weight) || 0), 0);
    
    const diffPcs = selectedCut ? selectedCut.pieces - totalPcs : 0;
    const diffWeight = selectedCut ? selectedCut.weight - totalWeight : 0;
    const isValid = selectedCut && diffPcs === 0 && Math.abs(diffWeight) < 0.01;

    const handleSubmit = async () => {
        if (!isValid) return alert("Totals do not match Parent Cut!");
        
        setIsSubmitting(true); // <--- DISABLE BUTTON
        try {
            await api.post('/kapans', { cutId: selectedCut._id, kapans: rows });
            alert("✅ Kapans Created Successfully!");
            window.location.reload();
        } catch (err) { 
            alert("Error: " + err.response?.data?.error); 
            setIsSubmitting(false); // Re-enable if error
        }
    };

    return (
        <div className="container mt-4 mb-5">
            <h3 className="fw-bold text-primary mb-4">🔪 Kapan Manager</h3>

            <ul className="nav nav-pills mb-4">
                <li className="nav-item"><button className={`nav-link ${tab==='new' ? 'active fw-bold':''}`} onClick={()=>setTab('new')}>✂️ Make New</button></li>
                <li className="nav-item"><button className={`nav-link ${tab==='list' ? 'active fw-bold':''}`} onClick={()=>setTab('list')}>📋 Kapan Stock</button></li>
            </ul>

            {tab === 'new' && (
                <>
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <label className="fw-bold mb-2">Select Active Cut</label>
                            <select className="form-select" onChange={handleCutSelect}>
                                <option value="">-- Choose Cut --</option>
                                {cuts.map(c => (
                                    <option key={c._id} value={c._id}>
                                        {c.fullCutId} | {c.pieces} Pcs | {c.weight} Cts | {c.origin}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {selectedCut && (
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                                <div>Target: <strong>{selectedCut.pieces} Pcs</strong> / <strong>{selectedCut.weight} Cts</strong></div>
                                <div>{isValid ? <span className="badge bg-success">MATCHED ✅</span> : <span className="badge bg-danger">MISMATCH ❌ ({diffWeight.toFixed(2)} Cts)</span>}</div>
                            </div>
                            
                            <div className="table-responsive">
                                <table className="table table-bordered align-middle text-center mb-0">
                                    <thead className="table-light">
                                        <tr><th width="15%">Name</th><th width="15%">Pcs</th><th width="15%">Weight</th><th width="40%">Description</th><th width="10%"></th></tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, idx) => (
                                            <tr key={idx}>
                                                <td><input className="form-control text-center fw-bold bg-light" value={row.name} readOnly /></td>
                                                <td><input type="number" className="form-control text-center" value={row.pieces} onChange={e => updateRow(idx, 'pieces', e.target.value)} /></td>
                                                <td><input type="number" className="form-control text-center fw-bold" value={row.weight} onChange={e => updateRow(idx, 'weight', e.target.value)} /></td>
                                                <td><input type="text" className="form-control" value={row.description} onChange={e => updateRow(idx, 'description', e.target.value)} /></td>
                                                <td><button className="btn btn-sm btn-outline-danger border-0" onClick={() => removeRow(idx)}>✖</button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="table-light fw-bold">
                                        <tr>
                                            <td className="text-end">Total:</td>
                                            <td className={diffPcs === 0 ? "text-success" : "text-danger"}>{totalPcs}</td>
                                            <td className={Math.abs(diffWeight) < 0.01 ? "text-success" : "text-danger"}>{totalWeight.toFixed(2)}</td>
                                            <td colSpan="2"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            <div className="card-footer p-3 d-flex justify-content-between">
                                <button className="btn btn-secondary" onClick={addRow} disabled={isSubmitting}>+ Add Kapan</button>
                                <button 
                                    className={`btn px-5 fw-bold ${isValid ? 'btn-success' : 'btn-secondary'}`} 
                                    disabled={!isValid || isSubmitting} 
                                    onClick={handleSubmit}
                                >
                                    {isSubmitting ? 'SAVING...' : 'SAVE KAPANS'}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {tab === 'list' && (
                <div className="d-flex flex-column gap-3">
                    {Object.keys(groupedKapans).length === 0 ? (
                        <div className="alert alert-secondary text-center">No Kapans Found.</div>
                    ) : (
                        Object.entries(groupedKapans).map(([cutId, group]) => (
                            <div key={cutId} className="card shadow-sm border-0">
                                <div className="card-header bg-white border d-flex justify-content-between align-items-center py-3" onClick={() => toggleGroup(cutId)} style={{cursor: 'pointer'}}>
                                    <div className="d-flex align-items-center gap-3">
                                        <button className="btn btn-sm btn-light border fw-bold text-muted">{expandedGroups[cutId] ? '▼' : '►'}</button>
                                        <div>
                                            <h6 className="mb-0 fw-bold text-primary">{group.parentName}</h6>
                                            <div className="small text-muted">{group.items.length} Kapans</div>
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <span className="badge bg-light text-dark border me-2">{group.totalPcs} Pcs</span>
                                        <span className="badge bg-success">{group.totalWeight.toFixed(2)} Cts</span>
                                    </div>
                                </div>

                                {expandedGroups[cutId] && (
                                    <div className="card-body p-0">
                                        <table className="table table-striped table-hover mb-0 align-middle text-center table-sm">
                                            <thead className="table-light">
                                                <tr><th>Name</th><th>ID</th><th>Pcs</th><th>Weight</th><th>Status</th><th>Description</th><th>Action</th></tr>
                                            </thead>
                                            <tbody>
                                                {group.items.map(k => (
                                                    <tr key={k._id}>
                                                        <td className="fw-bold">{k.kapanName}</td>
                                                        <td className="small text-muted">{k.fullKapanId}</td>
                                                        <td>{k.pieces}</td>
                                                        <td className="fw-bold">{k.weight}</td>
                                                        <td><span className="badge bg-info text-dark" style={{fontSize:'0.7rem'}}>{k.status}</span></td>
                                                        <td className="small text-muted text-start ps-3">{k.description}</td>
                                                        <td>
                                                            <button className="btn btn-sm text-danger" onClick={() => handleDeleteKapan(k._id)}>🗑️</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default MakeKapan;